import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RouteBuilders } from "../../classes/constants/routeConstants";
import { useAppContext } from "../../classes/contexts/AppContext";
import { usePatientContext } from "../../classes/contexts/PatientContext";
import { useRemoteRenderContext } from "../../classes/contexts/RemoteRenderContext";
import { DigitalTwinPageModalities } from "../../classes/enums/DigitalTwinEnum";
import {
  MorphologyOrientation,
  SPINESIM_MODALITIES,
} from "../../classes/enums/MedicalImagesEnum";
import { formatDateAsMonthYear } from "../../classes/utils/DateUtils";
import useTranslation from "../../hooks/i18n/useTranslation";
import { useDigitalTwinData } from "../../hooks/queries/useDigitalTwinData";
import { usePatientDigitalTwins } from "../../hooks/queries/usePatientDigitalTwins";
import { usePatientInstancesByModality } from "../../hooks/queries/usePatientInstancesByModality";
import { useMultiTwinValidation } from "../../hooks/useMultiTwinValidation";
import { usePatientMorphologyImages } from "../../hooks/usePatientMorphologyImages";
import { useRouteState } from "../../hooks/useRouteState";
import DigitalTwinCreationWidget from "./components/digitalTwinCreationWidget/digitalTwinCreationWidget";
import DigitalTwinDataInputs from "./components/digitalTwinDataInputs/digitalTwinDataInputs";
import DigitalTwinPlacheholder from "./components/digitalTwinPlaceholder/digitalTwinPlaceholder";
import DigitalTwinTimeline from "./components/digitalTwinTimeline/digitalTwinTimeline";
import DigitalTwinVisualization from "./components/digitalTwinVIsualization/digitalTwinVisualization";
import "./DigitalTwin.css";

const DigitalTwin = () => {
  const { hideSidebars } = useAppContext();
  const { fetchPatientImages } = usePatientContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { client: remoteRenderClient } = useRemoteRenderContext();
  const { t } = useTranslation();
  const { params } = useRouteState();

  const [pageModality, setPageModality] = useState();

  const [creationStep, setCreationStep] = useState(0);
  const [selectedDataItems, setSelectedDataItems] = useState({
    [SPINESIM_MODALITIES.DX]: [], // "DX"
    [SPINESIM_MODALITIES.CT]: [], // "CT"
    [SPINESIM_MODALITIES.MR]: [], // "MR"
    [SPINESIM_MODALITIES.POSTURE]: [], // "Posture"
    [SPINESIM_MODALITIES.PAIN]: [], // "Pain"
    [SPINESIM_MODALITIES.QUALITY]: [], // "Quality"
    [SPINESIM_MODALITIES.CLINICAL_HISTORY]: [], // "ClinicalHistory"
  });
  const [selectedTwin, setSelectedTwin] = useState(null);
  const [pendingTwinId, setPendingTwinId] = useState(null);
  const visualizationRef = useRef(null);

  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonModeEnabled, setComparisonModeEnabled] = useState(false);
  const [comparedTwin, setComparedTwin] = useState(null);
  const [pendingCompareId, setPendingCompareId] = useState(null);

  const [editingTwin, setEditingTwin] = useState(null);
  const [lockedSelections, setLockedSelections] = useState({});
  const editInitializedForTwinRef = useRef(null);

  const currentMode = useMemo(() => {
    const path = location.pathname;

    // Check for edit mode FIRST (most specific)
    if (path.includes("/edit")) {
      return DigitalTwinPageModalities.EDIT;
    }

    if (path.includes(`/${DigitalTwinPageModalities.CREATION}`))
      return DigitalTwinPageModalities.CREATION;
    if (path.includes(`/${DigitalTwinPageModalities.SELECTION}`))
      return DigitalTwinPageModalities.SELECTION;
    if (params.twinId && params.compareId)
      return DigitalTwinPageModalities.SELECTED;
    if (params.twinId) return DigitalTwinPageModalities.SELECTED;
    return DigitalTwinPageModalities.SELECTION;
  }, [location.pathname, params.twinId, params.compareId]);

  const {
    patientDigitalTwinIds,
    fetchPatientDigitalTwinIds,
    loading: idsLoading,
  } = usePatientDigitalTwins(params.patientId);

  const { digitalTwins, loading: twinsLoading } = useDigitalTwinData(
    patientDigitalTwinIds
  );

  // Get validation status for all twins
  const { validationStatus: twinValidationStatus } = useMultiTwinValidation(
    patientDigitalTwinIds
  );

  const { assessmentsByDate: painAssessments, clinicalHistories } =
    usePatientContext();
  const { imagesWithUrls: posturalImages } = usePatientMorphologyImages();
  const { instancesByModality, loading: instancesLoading } =
    usePatientInstancesByModality();

  // Define getTwinName before it's used in formattedTwins
  const getTwinName = ({ oldest_date, newest_date }) => {
    const oldestDate = formatDateAsMonthYear(oldest_date);
    const newestDate = formatDateAsMonthYear(newest_date);
    if (!oldestDate || !newestDate) return t("346");
    if (oldestDate === newestDate) return `${oldestDate}`;
    if (oldestDate.slice(-2) === newestDate.slice(-2)) {
      return `${oldestDate.slice(0, -2)}-${newestDate}`;
    }
    return `${oldestDate}-${newestDate}`;
  };

  // Define formattedTwins before it's used in useEffects
  const formattedTwins = useMemo(() => {
    if (twinsLoading || !digitalTwins) {
      return {};
    }

    const formatted = digitalTwins.reduce((acc, twin) => {
      if (!twin) return acc;
      acc[twin._id] = {
        id: twin._id,
        title: getTwinName({
          oldest_date: twin.date_of_oldest_file,
          newest_date: twin.date_of_newest_file,
        }),
        name: getTwinName({
          oldest_date: twin.date_of_oldest_file,
          newest_date: twin.date_of_newest_file,
        }),
        latestDate: twin.date_of_newest_file,
        oldestDate: twin.date_of_oldest_file,
        creationDate: twin.date_of_creation,
        date_of_oldest_file: twin.date_of_oldest_file,
        date_of_newest_file: twin.date_of_newest_file,
        coronal_dx_instance_id: twin.coronal_dx_instance_id || null,
        sagittal_dx_instance_id: twin.sagittal_dx_instance_id || null,
        ct_instance_id: twin.ct_instance_id || null,
        mri_instance_id: twin.mri_instance_id || null,
        back_morphology_picture: twin.back_morphology_picture || null,
        front_morphology_picture: twin.front_morphology_picture || null,
        left_morphology_picture: twin.left_morphology_picture || null,
        right_morphology_picture: twin.right_morphology_picture || null,
        pain_assessment: twin.pain_assessment || null,
        clinical_history_id: twin.clinical_history_id || null,
      };
      return acc;
    }, {});

    return formatted;
  }, [digitalTwins, twinsLoading]);

  useEffect(() => {
    if (currentMode === DigitalTwinPageModalities.CREATION) {
      setPageModality(DigitalTwinPageModalities.CREATION);
    } else if (currentMode === DigitalTwinPageModalities.EDIT) {
      // Set pageModality immediately for edit mode
      // Data initialization happens in separate useEffect below
      setPageModality(DigitalTwinPageModalities.EDIT);
    } else if (
      currentMode === DigitalTwinPageModalities.SELECTED &&
      params.twinId
    ) {
      setPageModality(DigitalTwinPageModalities.SELECTED);
      setPendingTwinId(params.twinId);
      if (params.compareId) {
        setPendingCompareId(params.compareId);
      }
    } else if (!idsLoading) {
      const modality =
        patientDigitalTwinIds.length > 0
          ? DigitalTwinPageModalities.SELECTION
          : DigitalTwinPageModalities.EMPTY;
      setPageModality(modality);
    }
  }, [
    currentMode,
    params.twinId,
    params.compareId,
    idsLoading,
    patientDigitalTwinIds,
  ]);

  useEffect(() => {
    hideSidebars();
  }, [hideSidebars]);

  useEffect(() => {
    if (!params.patientId) return;
    fetchPatientDigitalTwinIds();
    fetchPatientImages();
  }, [params.patientId, fetchPatientDigitalTwinIds, fetchPatientImages]);

  // Define initializeEditMode before it's used
  const initializeEditMode = useCallback(
    twin => {
      const { DX = [], CT = [], MR = [] } = instancesByModality || {};

      const initialSelections = {
        [SPINESIM_MODALITIES.DX]: [],
        [SPINESIM_MODALITIES.CT]: [],
        [SPINESIM_MODALITIES.MR]: [],
        [SPINESIM_MODALITIES.POSTURE]: [],
        [SPINESIM_MODALITIES.PAIN]: [],
        [SPINESIM_MODALITIES.QUALITY]: [],
        [SPINESIM_MODALITIES.CLINICAL_HISTORY]: [],
      };

      const locked = { ...initialSelections };

      // Map existing DX instances
      if (twin.sagittal_dx_instance_id) {
        const inst = DX?.find(i => i._id === twin.sagittal_dx_instance_id);
        if (inst) {
          initialSelections[SPINESIM_MODALITIES.DX].push(inst);
          locked[SPINESIM_MODALITIES.DX].push(inst._id);
        }
      }

      if (twin.coronal_dx_instance_id) {
        const inst = DX?.find(i => i._id === twin.coronal_dx_instance_id);
        if (inst) {
          initialSelections[SPINESIM_MODALITIES.DX].push(inst);
          locked[SPINESIM_MODALITIES.DX].push(inst._id);
        }
      }

      // Map CT
      if (twin.ct_instance_id) {
        const inst = CT?.find(i => i._id === twin.ct_instance_id);
        if (inst) {
          initialSelections[SPINESIM_MODALITIES.CT].push(inst);
          locked[SPINESIM_MODALITIES.CT].push(inst._id);
        }
      }

      // Map MR
      if (twin.mri_instance_id) {
        const inst = MR?.find(i => i._id === twin.mri_instance_id);
        if (inst) {
          initialSelections[SPINESIM_MODALITIES.MR].push(inst);
          locked[SPINESIM_MODALITIES.MR].push(inst._id);
        }
      }

      // Map posture images
      const postureFields = [
        {
          field: "front_morphology_picture",
          orientation: MorphologyOrientation.FRONT,
        },
        {
          field: "back_morphology_picture",
          orientation: MorphologyOrientation.BACK,
        },
        {
          field: "left_morphology_picture",
          orientation: MorphologyOrientation.LEFT,
        },
        {
          field: "right_morphology_picture",
          orientation: MorphologyOrientation.RIGHT,
        },
      ];

      postureFields.forEach(({ field, orientation }) => {
        if (twin[field]) {
          const img = posturalImages?.find(p => p.title === twin[field]);
          if (img) {
            initialSelections[SPINESIM_MODALITIES.POSTURE].push(img);
            locked[SPINESIM_MODALITIES.POSTURE].push(img.url);
          }
        }
      });

      // Map pain assessment
      if (twin.pain_assessment) {
        const pain = painAssessments?.find(p => p._id === twin.pain_assessment);
        if (pain) {
          initialSelections[SPINESIM_MODALITIES.PAIN].push(pain);
          locked[SPINESIM_MODALITIES.PAIN].push(pain._id);
        }
      }

      // Map clinical history
      if (twin.clinical_history_id) {
        const history = clinicalHistories?.find(
          h => h._id === twin.clinical_history_id
        );
        if (history) {
          initialSelections[SPINESIM_MODALITIES.CLINICAL_HISTORY].push(history);
          locked[SPINESIM_MODALITIES.CLINICAL_HISTORY].push(history._id);
        }
      }

      setSelectedDataItems(initialSelections);
      setLockedSelections(locked);
      setCreationStep(0);
    },
    [instancesByModality, posturalImages, painAssessments, clinicalHistories]
  );

  // Initialize edit mode data (after initializeEditMode is defined)
  // Note: pageModality is already set by the first useEffect above
  useEffect(() => {
    if (currentMode === DigitalTwinPageModalities.EDIT && params.twinId) {
      // Skip if already initialized for this twin (prevents reset on instancesByModality refetch)
      if (editInitializedForTwinRef.current === params.twinId) return;

      // Wait for all required data to load before initializing
      if (
        !twinsLoading &&
        !instancesLoading &&
        digitalTwins &&
        digitalTwins.length > 0 &&
        instancesByModality
      ) {
        // Only proceed if twins AND instances have loaded
        const twinToEdit = formattedTwins[params.twinId];

        if (!twinToEdit) {
          // Twin not found after data loaded, redirect to selection
          navigate(RouteBuilders.digitalTwinSelection(params.patientId));
          return;
        }
        // Mark as initialized before setting state to prevent double-run
        editInitializedForTwinRef.current = params.twinId;
        // Initialize edit data
        setEditingTwin(twinToEdit);
        initializeEditMode(twinToEdit);
      }
    } else if (currentMode !== DigitalTwinPageModalities.EDIT) {
      // Clear the ref when leaving edit mode so re-entering will re-initialize
      editInitializedForTwinRef.current = null;
    }
  }, [
    currentMode,
    params.twinId,
    twinsLoading,
    instancesLoading,
    digitalTwins,
    formattedTwins,
    instancesByModality,
    navigate,
    params.patientId,
    initializeEditMode,
  ]);

  // When posturalImages refreshes (fetchPatientImages revokes old blob URLs and creates new ones),
  // update posture items in selectedDataItems so URLs stay valid without resetting other state.
  useEffect(() => {
    if (!posturalImages?.length) return;
    setSelectedDataItems(prev => {
      const postureItems = prev[SPINESIM_MODALITIES.POSTURE];
      if (!postureItems?.length) return prev;

      let changed = false;
      const updated = postureItems.map(item => {
        const fresh =
          posturalImages.find(
            p => p.title === item.title && p.orientation === item.orientation
          ) || posturalImages.find(p => p.title === item.title);

        if (!fresh || fresh.url === item.url) return item;
        changed = true;

        return {
          ...item,
          url: fresh.url,
        };
      });

      if (!changed) return prev;
      return { ...prev, [SPINESIM_MODALITIES.POSTURE]: updated };
    });
  }, [posturalImages]);

  const activeTwins = formattedTwins;

  const setupComparisonMode = useCallback(
    (mainTwinId, comparisonTwinId) => {
      if (
        mainTwinId &&
        comparisonTwinId &&
        activeTwins[mainTwinId] &&
        activeTwins[comparisonTwinId]
      ) {
        setSelectedTwin(activeTwins[mainTwinId]);
        setComparedTwin(activeTwins[comparisonTwinId]);
        setComparisonMode(true);
        setComparisonModeEnabled(true); // Enable comparison mode toggle
        setPageModality(DigitalTwinPageModalities.SELECTED);
      }
    },
    [activeTwins]
  );

  const handleToggleComparisonMode = useCallback(() => {
    if (comparisonModeEnabled) {
      // Turning OFF comparison mode
      setComparisonModeEnabled(false);
      setComparisonMode(false);
      setComparedTwin(null);

      // Navigate back to single twin view
      if (selectedTwin) {
        navigate(
          RouteBuilders.digitalTwinSelected(params.patientId, selectedTwin.id)
        );
      }
    } else {
      // Turning ON comparison mode
      setComparisonModeEnabled(true);

      // Auto-select the first available twin (that's not the currently selected one) as the compared twin
      if (selectedTwin) {
        const availableTwins = Object.values(activeTwins).filter(
          twin => twin.id !== selectedTwin.id
        );

        if (availableTwins.length > 0) {
          // Sort by creation date (most recent first) and select the first one
          const sortedTwins = availableTwins.sort((a, b) => {
            const dateA = new Date(a.creationDate);
            const dateB = new Date(b.creationDate);
            return dateB - dateA;
          });

          const comparedTwinToSelect = sortedTwins[0];
          setComparedTwin(comparedTwinToSelect);
          setComparisonMode(true);
          navigate(
            RouteBuilders.digitalTwinComparison(
              params.patientId,
              selectedTwin.id,
              comparedTwinToSelect.id
            )
          );
        }
      }
    }
  }, [
    comparisonModeEnabled,
    selectedTwin,
    activeTwins,
    navigate,
    params.patientId,
  ]);

  const handleNewDigitalTwinClick = () => {
    cleanupState({ cleanCreation: true });
    setPageModality(DigitalTwinPageModalities.CREATION);
    navigate(RouteBuilders.digitalTwinCreation(params.patientId));
  };

  const handleSelectTwin = useCallback(
    id => {
      if (!id) {
        setSelectedTwin(null);
        setComparedTwin(null);
        setComparisonMode(false);
        setComparisonModeEnabled(false);
        setPageModality(DigitalTwinPageModalities.SELECTION);
        navigate(RouteBuilders.digitalTwinSelection(params.patientId));
        return;
      }

      if (comparisonModeEnabled) {
        // Comparison mode is enabled - keep it enabled
        if (comparisonMode) {
          // Already comparing twins
          if (selectedTwin?.id === id) {
            // Clicked on main twin - switch to make compared twin the main one
            setSelectedTwin(comparedTwin);
            setComparedTwin(null);
            setComparisonMode(false);
            // Keep comparisonModeEnabled as true
            navigate(
              RouteBuilders.digitalTwinSelected(
                params.patientId,
                comparedTwin.id
              )
            );
          } else if (comparedTwin?.id === id) {
            // Clicked on compared twin - remove it but keep comparison mode enabled
            setComparedTwin(null);
            setComparisonMode(false);
            // Keep comparisonModeEnabled as true
            navigate(
              RouteBuilders.digitalTwinSelected(
                params.patientId,
                selectedTwin.id
              )
            );
          } else {
            // Clicked on a third twin - replace compared twin
            setComparedTwin(activeTwins[id]);
            // comparisonMode stays true, comparisonModeEnabled stays true
            navigate(
              RouteBuilders.digitalTwinComparison(
                params.patientId,
                selectedTwin.id,
                id
              )
            );
          }
        } else {
          // Comparison mode enabled but no compared twin yet
          if (selectedTwin?.id === id) {
            // Clicked on already selected twin - do nothing
            return;
          }
          // Set the compared twin
          setComparedTwin(activeTwins[id]);
          setComparisonMode(true);
          // Keep comparisonModeEnabled as true
          navigate(
            RouteBuilders.digitalTwinComparison(
              params.patientId,
              selectedTwin.id,
              id
            )
          );
        }
      } else {
        // Comparison mode disabled - normal twin switching
        setSelectedTwin(activeTwins[id]);
        setPageModality(DigitalTwinPageModalities.SELECTED);
        navigate(RouteBuilders.digitalTwinSelected(params.patientId, id));
      }
    },
    [
      activeTwins,
      comparisonMode,
      comparisonModeEnabled,
      selectedTwin,
      comparedTwin,
      navigate,
      params.patientId,
    ]
  );

  const handleBackToSelection = () => {
    cleanupState({ all: true });
    setPageModality(DigitalTwinPageModalities.SELECTION);
    navigate(RouteBuilders.digitalTwinSelection(params.patientId));
  };

  const handleTwinGeneration = newTwinId => {
    setPendingTwinId(newTwinId);
    cleanupState({ cleanCreation: true });
    fetchPatientDigitalTwinIds();
  };

  const handleTwinUpdate = async updatedTwinId => {
    await fetchPatientDigitalTwinIds();
    cleanupState({ cleanCreation: true, cleanEdit: true });
    setPageModality(DigitalTwinPageModalities.SELECTED);
    navigate(
      RouteBuilders.digitalTwinSelected(params.patientId, updatedTwinId)
    );
  };

  const handleTwinDeleted = useCallback(
    async deletedTwinId => {
      await fetchPatientDigitalTwinIds();

      const isDeletedTwinActive =
        selectedTwin?.id === deletedTwinId ||
        comparedTwin?.id === deletedTwinId ||
        params.twinId === deletedTwinId ||
        params.compareId === deletedTwinId;

      if (!isDeletedTwinActive) return;

      setSelectedTwin(null);
      setComparedTwin(null);
      setComparisonMode(false);
      setComparisonModeEnabled(false);
      setPageModality(DigitalTwinPageModalities.SELECTION);
      navigate(RouteBuilders.digitalTwinSelection(params.patientId));
    },
    [
      fetchPatientDigitalTwinIds,
      selectedTwin,
      comparedTwin,
      params.twinId,
      params.compareId,
      params.patientId,
      navigate,
    ]
  );

  useEffect(() => {
    if (!pendingTwinId) return;

    if (twinsLoading) return;
    if (!formattedTwins[pendingTwinId]) return;

    if (pendingCompareId) {
      if (!formattedTwins[pendingCompareId]) return;

      setupComparisonMode(pendingTwinId, pendingCompareId);
    } else {
      handleSelectTwin(pendingTwinId);
    }

    setPendingTwinId(null);
    setPendingCompareId(null);
  }, [
    pendingTwinId,
    pendingCompareId,
    twinsLoading,
    formattedTwins,
    setupComparisonMode,
    handleSelectTwin,
  ]);

  const cleanupState = useCallback((options = {}) => {
    const {
      cleanCreation = false,
      cleanEdit = false,
      cleanComparison = false,
      cleanSelection = false,
      all = false,
    } = options;

    if (cleanCreation || cleanEdit || all) {
      setCreationStep(0);
      setSelectedDataItems({
        [SPINESIM_MODALITIES.DX]: [],
        [SPINESIM_MODALITIES.CT]: [],
        [SPINESIM_MODALITIES.MR]: [],
        [SPINESIM_MODALITIES.POSTURE]: [],
        [SPINESIM_MODALITIES.PAIN]: [],
        [SPINESIM_MODALITIES.QUALITY]: [],
        [SPINESIM_MODALITIES.CLINICAL_HISTORY]: [],
      });
    }

    if (cleanEdit || all) {
      setEditingTwin(null);
      setLockedSelections({});
    }

    if (cleanComparison || all) {
      setComparisonMode(false);
      setComparedTwin(null);
      setPendingCompareId(null);
      // Only reset comparisonModeEnabled when doing a full cleanup (all: true)
      if (all) {
        setComparisonModeEnabled(false);
      }
    }

    if (cleanSelection || all) {
      setSelectedTwin(null);
      setPendingTwinId(null);
    }
  }, []);

  useEffect(() => {
    switch (pageModality) {
      case DigitalTwinPageModalities.EMPTY:
      case DigitalTwinPageModalities.SELECTION:
        cleanupState({
          cleanCreation: true,
          cleanEdit: true,
          cleanComparison: true,
        });
        break;

      case DigitalTwinPageModalities.SELECTED:
        cleanupState({ cleanCreation: true, cleanEdit: true });
        break;

      case DigitalTwinPageModalities.CREATION:
        cleanupState({ cleanComparison: true, cleanEdit: true });
        break;

      case DigitalTwinPageModalities.EDIT:
        cleanupState({ cleanComparison: true });
        break;

      default:
        break;
    }
  }, [pageModality, cleanupState]);

  useEffect(() => {
    if (pendingTwinId) {
      cleanupState({ cleanCreation: true });
    }
  }, [pendingTwinId, cleanupState]);

  useEffect(() => {
    return () => {
      cleanupState({ all: true });
    };
  }, [cleanupState]);

  const getContainerClass = () =>
    `Digital-twin-widgets-container ${pageModality}`;

  const renderContent = () => {
    switch (pageModality) {
      case DigitalTwinPageModalities.EMPTY:
      case DigitalTwinPageModalities.SELECTION:
        return (
          <>
            <DigitalTwinTimeline
              twins={activeTwins}
              onTwinSelect={handleSelectTwin}
              selectedTwinId={selectedTwin?.id}
              comparedTwinId={comparedTwin?.id}
              comparisonMode={comparisonMode}
              visualizationRef={visualizationRef}
              twinValidationStatus={twinValidationStatus}
            />
            <DigitalTwinPlacheholder
              OnNewDigitalTwinClick={handleNewDigitalTwinClick}
            />
          </>
        );
      case DigitalTwinPageModalities.SELECTED:
        return (
          <>
            <DigitalTwinTimeline
              twins={activeTwins}
              onTwinSelect={handleSelectTwin}
              selectedTwinId={selectedTwin?.id}
              comparedTwinId={comparedTwin?.id}
              comparisonMode={comparisonMode}
              twinValidationStatus={twinValidationStatus}
            />
            <DigitalTwinVisualization
              selectedTwin={selectedTwin}
              comparedTwin={comparedTwin}
              comparisonMode={comparisonMode}
              comparisonModeEnabled={comparisonModeEnabled}
              renderClient={remoteRenderClient}
              onToggleComparisonMode={handleToggleComparisonMode}
              onDeleteTwin={handleTwinDeleted}
            />
          </>
        );
      case DigitalTwinPageModalities.CREATION:
      case DigitalTwinPageModalities.EDIT:
        return (
          <>
            <DigitalTwinDataInputs
              onGoBack={handleBackToSelection}
              onGenerateTwin={
                pageModality === DigitalTwinPageModalities.EDIT
                  ? handleTwinUpdate
                  : handleTwinGeneration
              }
              selectedStep={creationStep}
              setSelectedStep={setCreationStep}
              selectedItems={selectedDataItems}
              setSelectedItems={setSelectedDataItems}
              isEditMode={pageModality === DigitalTwinPageModalities.EDIT}
              editingTwin={editingTwin}
              lockedSelections={lockedSelections}
            />
            <DigitalTwinCreationWidget
              creationStep={creationStep}
              selectedItems={selectedDataItems}
              renderClient={remoteRenderClient}
            />
          </>
        );
      default:
        return null;
    }
  };

  return <div className={getContainerClass()}>{renderContent()}</div>;
};

export default DigitalTwin;
